import type {
  DayCategoryCompletion,
  DayCategoryStatus,
  ParseEmailExtracted,
} from '@travel-planner/shared/schemas';
import type {
  DayOverrides,
  GeneratorSelections,
} from '../../model/use-agent-panel-store';
import { UTCDate } from '@date-fns/utc';

// The four product categories the day-picks UI tabs across, in display
// order (Stay first, matching the pen `oomtC` tab bar).
export type DayCategory =
  | 'transportation'
  | 'accommodation'
  | 'activities'
  | 'dining';

export const DAY_CATEGORY_ORDER: ReadonlyArray<DayCategory> = [
  'accommodation',
  'activities',
  'dining',
  'transportation',
];

// Required pick count per category for a single day to count as "complete".
// Deterministic baseline (PRD FR-5) — no LLM call. Accommodation needs a
// single stay; activities benefit from two; transport / dining need one.
const REQUIRED_PER_DAY: Record<DayCategory, number> = {
  transportation: 1,
  accommodation: 1,
  activities: 2,
  dining: 1,
};

/**
 * Trip length in days. Prefers the sum of destination nights, falls back to
 * the start/end date span, finally defaults to 1 so the day picker always
 * has at least one day to render.
 */
export const deriveDurationDays = (extracted: ParseEmailExtracted): number => {
  const nights = extracted.destinations.reduce(
    (sum, d) => sum + (d.nights ?? 0),
    0
  );
  if (nights > 0) return nights;
  if (extracted.startDate && extracted.endDate) {
    const start = new UTCDate(`${extracted.startDate}T00:00:00.000Z`);
    const end = new UTCDate(`${extracted.endDate}T00:00:00.000Z`);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const span = Math.round((end.getTime() - start.getTime()) / 86_400_000);
      if (span > 0) return span;
    }
  }
  return 1;
};

const completionFor = (
  picked: number,
  required: number
): DayCategoryCompletion => {
  if (picked >= required) return 'complete';
  if (picked > 0) return 'partial';
  return 'missing';
};

// Picks effective for a given day: a per-day override wins over the
// brief-wide selections when present (mirrors buildFromSelections.distribute).
const pickedCountsForDay = (
  selections: GeneratorSelections,
  dayOverrides: DayOverrides,
  dayIndex: number
) => {
  const override = dayOverrides[String(dayIndex)];
  const accommodationId =
    override?.accommodationId !== undefined
      ? override.accommodationId
      : selections.accommodationId;
  return {
    transport: (override?.transportationIds ?? selections.transportationIds)
      .length,
    accommodation: accommodationId ? 1 : 0,
    activities: (override?.activityIds ?? selections.activityIds).length,
    dining: (override?.diningIds ?? selections.diningIds).length,
  };
};

/**
 * The set of product IDs effectively picked for `dayIndex` on `category` —
 * the day-aware sibling of `selectedIdsForCategory(selections, …)`. Reads
 * the per-day `dayOverrides` first and falls back to the brief-wide
 * selections, exactly like `pickedCountsForDay` / `buildFromSelections`.
 *
 * Accommodation is treated as a single brief-wide stay (one hotel covers
 * the trip), so it always reads from `selections` unless a day explicitly
 * overrides it.
 */
export const effectivePickedIdsForDay = (
  selections: GeneratorSelections,
  dayOverrides: DayOverrides,
  dayIndex: number,
  category: DayCategory
): ReadonlySet<string> => {
  const override = dayOverrides[String(dayIndex)];
  switch (category) {
    case 'accommodation': {
      const id =
        override?.accommodationId !== undefined
          ? override.accommodationId
          : selections.accommodationId;
      return new Set(id ? [id] : []);
    }
    case 'activities':
      return new Set(override?.activityIds ?? selections.activityIds);
    case 'dining':
      return new Set(override?.diningIds ?? selections.diningIds);
    case 'transportation':
      return new Set(
        override?.transportationIds ?? selections.transportationIds
      );
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

/**
 * Derive the per-day `DayCategoryStatus` from the consultant's current
 * picks. The analysis `categoryStatus` is a baseline; once the consultant
 * starts picking, the client-side derivation is the live source of truth.
 *
 * Day 0 carries the accommodation pick (a single stay covers the whole
 * trip in the v1 allocation), so other days only need transport / activity
 * / dining picks to be "complete" on accommodation by inheritance.
 */
export const deriveCategoryStatus = (
  durationDays: number,
  selections: GeneratorSelections,
  dayOverrides: DayOverrides
): ReadonlyArray<DayCategoryStatus> => {
  const result: DayCategoryStatus[] = [];
  for (let dayIndex = 0; dayIndex < durationDays; dayIndex++) {
    const picked = pickedCountsForDay(selections, dayOverrides, dayIndex);
    const requiredCounts = {
      transport: REQUIRED_PER_DAY.transportation,
      accommodation: REQUIRED_PER_DAY.accommodation,
      activities: REQUIRED_PER_DAY.activities,
      dining: REQUIRED_PER_DAY.dining,
    };
    result.push({
      dayIndex,
      transport: completionFor(picked.transport, requiredCounts.transport),
      accommodation: completionFor(
        picked.accommodation,
        requiredCounts.accommodation
      ),
      activities: completionFor(picked.activities, requiredCounts.activities),
      dining: completionFor(picked.dining, requiredCounts.dining),
      pickedCounts: picked,
      requiredCounts,
    });
  }
  return result;
};

// Map the UI `DayCategory` onto the `DayCategoryStatus` field names (the
// schema uses `transport` while the UI uses `transportation`).
export const completionForCategory = (
  status: DayCategoryStatus,
  category: DayCategory
): DayCategoryCompletion => {
  switch (category) {
    case 'transportation':
      return status.transport;
    case 'accommodation':
      return status.accommodation;
    case 'activities':
      return status.activities;
    case 'dining':
      return status.dining;
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

export const pickedCountForCategory = (
  status: DayCategoryStatus,
  category: DayCategory
): { picked: number; required: number } => {
  switch (category) {
    case 'transportation':
      return {
        picked: status.pickedCounts.transport,
        required: status.requiredCounts.transport,
      };
    case 'accommodation':
      return {
        picked: status.pickedCounts.accommodation,
        required: status.requiredCounts.accommodation,
      };
    case 'activities':
      return {
        picked: status.pickedCounts.activities,
        required: status.requiredCounts.activities,
      };
    case 'dining':
      return {
        picked: status.pickedCounts.dining,
        required: status.requiredCounts.dining,
      };
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

// Count of incomplete categories on a day — drives the day-picker menu's
// per-day gap badges and the progress caption.
export const dayGapCount = (status: DayCategoryStatus): number =>
  [
    status.transport,
    status.accommodation,
    status.activities,
    status.dining,
  ].filter((c) => c !== 'complete').length;
